define((require) => {
    "use strict";

    const _                                = require("underscore");
    const DragSelect                       = require("dragselect");
    const AddWidgetsAction                 = require("ui/actions/AddWidgetsAction");
    const BackdropViewer                   = require("ui/editor/BackdropViewer");
    const DropAreaHotspot                  = require("ui/behaviors/DropAreaHotspot");
    const EditorActivities                 = require("editor/ui/experience/editor/activity/EditorActivities");
    const EditorActivityManager            = require("editor/ui/experience/editor/activity/EditorActivityManager");
    const EditorEvents                     = require("shared/services/EditorEvents");
    const Effect                           = require("shared/ui/experience/action/effects/EffectModel");
    const ImprovedView                     = require("editor/zbone/ImprovedView");
    const {Logger}                         = require("shared/util/Logger");
    const SceneDropHandler                 = require("ui/editor/SceneDropHandler");
    const SceneEditorLoadingScreen         = require("ui/editor/sceneLoadingScreen/SceneEditorLoadingScreen");
    const SceneRulers                      = require("ui/editor/ruler/SceneRulers");
    const SceneWidgetCollectionViewsLayout = require("ui/editor/SceneWidgetCollectionViewsLayout");
    const Snap                             = require("services/snap/Snap");
    const SyncManager                      = require("shared/services/sync/SyncManager");
    const template                         = require("ui/editor/SceneEditor.tpl");
    const Tooltip                          = require("ui/common/Tooltip");
    const WidgetGroup                      = require("ui/editor/WidgetGroup");
    const WidgetTypes                      = require("shared/ui/experience/widgets/WidgetTypes");
    const EditorEventFilters               = require("ui/editor/EditorEventFilters").default;
    const {mapWidgetMetaData}              = require("ui/editor/SceneEditorHelpers");
    const NotificationBar                  = require("editor/ui/notificationbar/NotificationBar");
    const ImageToggleEffectModel           = require("shared/ui/experience/action/effects/ImageToggleEffectModel");
    const OpacityEffectModel               = require("shared/ui/experience/action/effects/OpacityEffectModel");

    const log = new Logger("SceneEditor");
    const SCENE_MARGIN = 20;

    return ImprovedView.extend({

        tagName: "div",

        template,

        templateHelpers() {
            return {
                widgetBoundaryClass: this.viewModel.get("showMasterLayers") ? "master-layers-on" : "master-layers-off",
                spectatorMode: this.viewScope.spectatorMode
            };
        },

        className: "scene-editor",

        experienceModel: null,

        // the editor view model
        viewModel: null,

        _dropHandler: null,

        _sceneClassName: "",

        sceneWidgetCollectionView: null,

        dragSelect: null,

        dragSelectMouseCoords: null,

        ui: {
            guidelinesContainer: ".guidelines-container",
            widgetContainer: ".widgets-container",
            containerBorder: ".container-border",
            sceneFooter: ".scene-footer",
            moveSceneForwards: ".move-scene-forwards",
            moveSceneBackwards: ".move-scene-backwards",
            widgetsBoundary: ".widgets-boundary",
            horizontalRuler: ".horizontal-ruler",
            verticalRuler: ".vertical-ruler",

            snapReplacement: ".snap-replacement",
            snapLineVertical: ".snap-line.vertical",
            snapLineHorizontal: ".snap-line.horizontal",
            snapCross: ".snap-cross",
            deleteScene: ".delete-scene",
            duplicateScene: ".duplicate-scene",
            sceneWidgetCollectionViewsRegionContainer: ".scene-widget-collection-views-region-container"
        },

        regions: {
            sceneWidgetCollectionViewsRegion: ".scene-widget-collection-views-region-container",
            backdropRegion: ".master-layer.backdrop",
            guidelinesContainer: ".guidelines-container",
            loadingRegion: ".scene-loading-region"
        },

        events: {
            "click @ui.deleteScene": "_deleteSceneClickHandler",
            "click @ui.duplicateScene": "duplicate",
            "mousedown @ui.widgetContainer": "handleMouseDownEvent",
            "drop @ui.widgetContainer": "handleDropEvent",
            dragover: "_onDragOver",
            "click @ui.moveSceneForwards": "_onClickMoveSceneForwards",
            "click @ui.moveSceneBackwards": "_onClickMoveSceneBackwards"
        },

        _onChangeSceneNumber() {
            // Whenever we switch scenes, we should clear any widget selection
            this.viewModel.selection.clear();
            this._updateToCurrentScene();
        },

        modelEvents: {
            "addWidgets": "_addWidgetsHandler",
            "selectWidgets": "_selectWidgetHandler",
            "change:id": "_updateToCurrentScene",
            "change:sceneNumber": "_onModelChangeSceneNumber",
            // Both of these calls need to happen because we can not guarantee if each will happen and in what order.
            "change:widgetTabOrder": "_onModelChangeWidgetTabOrder",
            "change:removedWidgetTabOrder": "_onModelChangeRemovedWidgetTabOrder",
            "change:widgetTabOrderMetaData": "_onModelChangeWidgetTabOrderMetaData",
            "change:removedWidgetTabOrderMetaData": "_onModelChangeRemovedWidgetTabOrderMetaData"
        },

        bindings: {
            ".scene-index": "sceneNumber"
        },

        behaviors: {
            dropAreaHotspot: {
                behaviorClass: DropAreaHotspot,
                message: "<strong>Drop image(s)</strong> here to add directly<br>to Scene and Image Library",
                dropAreaSelector: ".widgets-container",
                iconSize: "large",
                uploadEnabled: false
            },

            snapping: {
                behaviorClass: Snap
            }
        },

        initialize({viewScope, experienceModel}) {
            this.experienceModel = experienceModel;
            this.viewModel = viewScope.viewModel;

            const sceneEditor = this;

            this._dropHandler = new SceneDropHandler(this);
            this.listenTo(this._dropHandler, SceneDropHandler.ADD_UPLOAD_BARS, this._addUploadBars);

            this.listenTo(this.experienceModel, "change:width change:height", this.render);

            this.listenTo(this.experienceModel, "change:width change:height", this._resetDragSelect);
            this.listenTo(this.experienceModel, "change:emailModeEnabled", () => {
                this._highlightEmailOverlappedWidgets();
                this._resetWidgetAction();
                this._toggleSceneEnabled();
            });

            // Make sure no widgets are selected after a fetch, i.e. user action on error like "refresh",
            // since the old selection would be off sometimes, e.g. if the widgets have changed position
            // or what not...
            SyncManager.onSync({
                context: this,
                target:  this.experienceModel,
                method:  "fetch",
                callback() {
                    _.defer(() => {
                        sceneEditor.viewModel.selection.clear();
                    });
                }
            });

            this.listenTo(experienceModel.get("scenes"), "getEmailActionWidgetsBoundingBoxes", this._getEmailActionWidgetsBoundingBoxes);
            this.listenTo(experienceModel.get("overlayScene"), "selectWidgets", this._selectWidgetHandler);
            this.listenTo(experienceModel.get("backgroundScene"), "selectWidgets", this._selectWidgetHandler);

            this.listenTo(this.viewModel, {"change:sceneNumber": this._updateToCurrentScene});

            this.listenTo(this.viewModel, {"change:sceneNumber": this._initializeDragSelect});

            this.listenTo(this.viewModel, {"change:sceneNumber": this._onModelChangeSceneNumber});

            this.listenTo(this.viewModel.get("zoomModel"), {"change:scale": this._updateToCurrentScaleFactor});
            this.listenTo(this.viewModel, "change:currentLayer", this._onSceneLayerChanged);

            this.sceneRulers = new SceneRulers({sceneEditor: this, viewScope: this.options.viewScope});

            this.listenTo(this.model.get("widgets"), "change:isLocked", this._changeWidgetLockStatus);
            this.listenTo(experienceModel.get("overlayScene").get("widgets"), "change:isLocked", this._changeWidgetLockStatus);
            this.listenTo(experienceModel.get("backgroundScene").get("widgets"), "change:isLocked", this._changeWidgetLockStatus);

            this.listenTo(experienceModel.get("overlayScene"), "change:widgetTabOrder", this._onModelChangeWidgetTabOrder);
            this.listenTo(experienceModel.get("overlayScene"), "change:removedWidgetTabOrder", this._onModelChangeRemovedWidgetTabOrder);

            this.listenTo(experienceModel.get("backgroundScene"), "change:widgetTabOrder", this._onModelChangeWidgetTabOrder);
            this.listenTo(experienceModel.get("backgroundScene"), "change:removedWidgetTabOrder", this._onModelChangeRemovedWidgetTabOrder);

            this.listenTo(EditorEvents, "undo:activity redo:activity", this._resetDragSelect);
        },


        _changeWidgetLockStatus(widget) {
            _.defer(() => {
                this._resetDragSelect();
                this.dragSelect.clearSelection();

                // after widget is unlocked it should be selected
                if (!widget.get("isLocked")) {
                    this._selectWidgetHandler([widget]);
                    widget.trigger("forceWidgetUpdate");
                }
            });
        },

        _removeOverlapping(widget) {
            widget.set({overlapped: false});
        },

        _resetWidgetOverlapping() {
            const isEmailModeEnabled = this.experienceModel.get("emailModeEnabled");
            const resetOverlapping = (collection) => {
                collection.each((item) => {
                    if (item.model.get("group")) {
                        resetOverlapping(item.groupCollectionView.children);
                        return;
                    }
                    this._removeOverlapping(item.model);
                });
            };

            if (isEmailModeEnabled) {
                resetOverlapping(this.sceneWidgetCollectionView.sceneWidgetCollectionView.children);
                resetOverlapping(this.sceneWidgetCollectionView.overlayWidgetCollectionView.children);
                resetOverlapping(this.sceneWidgetCollectionView.backgroundWidgetCollectionView.children);
            }
        },

        _resetWidgetAction() {
            const isEmailModeEnabled = this.experienceModel.get("emailModeEnabled");
            let removedIncompatibleWidgetActionOrEffect = false;
            const resetAction = (collection) => {
                collection.each((item) => {
                    if (item.model.get("group")) {
                        resetAction(item.groupCollectionView.children);
                        return;
                    }
                    const action = item.model.get("action");
                    const effects = item.model.get("effects");

                    // Remove all attention effects (in background)
                    const attentionEffects = effects.findWhere({triggerType: Effect.TRIGGER_TYPE_ATTENTION});
                    if (attentionEffects) {
                        removedIncompatibleWidgetActionOrEffect = true;
                        effects.remove(attentionEffects);
                    }

                    // Remove inappropriate hover effects (in background)
                    // Only opacity and image swapping allowed.
                    const inappropriateHoverEffect = effects.models.find((effect) => {
                        return effect.get("type") !== ImageToggleEffectModel.TYPE && effect.get("type") !== OpacityEffectModel.TYPE;
                    });
                    if (inappropriateHoverEffect) {
                        removedIncompatibleWidgetActionOrEffect = true;
                        effects.remove(inappropriateHoverEffect);
                    }

                    if (action && action.get("type") !== "link") {
                        removedIncompatibleWidgetActionOrEffect = true;
                        item.model.set({action: null});
                    }
                });
            };

            if (isEmailModeEnabled) {
                resetAction(this.sceneWidgetCollectionView.sceneWidgetCollectionView.children);
                resetAction(this.sceneWidgetCollectionView.overlayWidgetCollectionView.children);
                resetAction(this.sceneWidgetCollectionView.backgroundWidgetCollectionView.children);
            }

            if (removedIncompatibleWidgetActionOrEffect) {
                NotificationBar.open("We have removed actions/animations that are not compatible with email mode from your widgets where applicable.", {closeDelay: 5000});
            }
        },

        _toggleSceneEnabled() {
            const isEmailModeEnabled = this.experienceModel.get("emailModeEnabled");
            const isDisabled = !this.viewModel.isOnSceneLayer();
            this.ui.duplicateScene.toggleClass("disabled", isDisabled || isEmailModeEnabled);
        },

        _showHideDisableDuplicateButtons() {
            const isDisabled = (!this.viewModel.isOnSceneLayer());

            this.ui.deleteScene.toggleClass("disabled", isDisabled);
            this.ui.duplicateScene.toggleClass("disabled", isDisabled);
            this.ui.moveSceneForwards.toggleClass("disabled", isDisabled);
            this.ui.moveSceneBackwards.toggleClass("disabled", isDisabled);

            Tooltip.remove(this.ui.deleteScene);
            Tooltip.remove(this.ui.duplicateScene);
            Tooltip.remove(this.ui.moveSceneForwards);
            Tooltip.remove(this.ui.moveSceneBackwards);

            if (isDisabled) {
                Tooltip.add(this.ui.deleteScene, {
                    content: "You can only delete the scene from the Scene layer",
                    placement: "bottom",
                    alignCenter: true,
                    offset: {
                        align: "center"
                    },
                    width: "auto",
                    delay: {show: 0, hide: 0}
                });
                Tooltip.add(this.ui.duplicateScene, {
                    content: "You can only duplicate the scene from the Scene layer",
                    placement: "bottom",
                    alignCenter: true,
                    offset: {
                        align: "center"
                    },
                    width: "auto",
                    delay: {show: 0, hide: 0}
                });

                Tooltip.add(this.ui.moveSceneForwards, {
                    content: "You can only move the scene from the Scene layer",
                    placement: "bottom",
                    alignCenter: true,
                    offset: {
                        align: "center"
                    },
                    width: "auto",
                    delay: {show: 0, hide: 0}
                });
                Tooltip.add(this.ui.moveSceneBackwards, {
                    content: "You can only move the scene from the Scene layer",
                    placement: "bottom",
                    alignCenter: true,
                    offset: {
                        align: "center"
                    },
                    width: "auto",
                    delay: {show: 0, hide: 0}
                });
            }
        },

        _onSceneLayerChanged() {
            this._setTabOrderData();
            this._showHideDisableDuplicateButtons();
            this._initializeDragSelect();
            this._toggleSceneEnabled();
        },

        _getDragSelectSelectables() {
            // Handle grouped widgets by removing widget-component elements that aren't direct
            // children of the element with the active-layer-widgets class from the selectables.
            // Have to do it in this convoluted way because DragSelect only accepts an HTMLCollection
            // or an array of HTMLElements but not a NodeList which would be returned by querySelectorAll().
            const selectableArray = Array.prototype.slice.call(document.getElementsByClassName("widget-component"));
            let selectables = [];
            switch (this.viewModel.get("currentLayer").value) {
                case "overlay":
                    selectables = selectableArray.filter((selectable) => {
                        return selectable.parentElement.classList.contains("widget-collection-view-container-overlay");
                    });
                    break;
                case "background":
                    selectables = selectableArray.filter((selectable) => {
                        return selectable.parentElement.classList.contains("widget-collection-view-container-background");
                    });
                    break;
                case "scene":
                    selectables = selectableArray.filter((selectable) => {
                        return selectable.parentElement.classList.contains("widget-collection-view-container-scene");
                    });
                    break;
                default:
                    break;
            }
            return selectables.filter((element) => !element.classList.contains("is-locked"));
        },

        _onModelChangeSceneNumber() {
            this._setTabOrderData();
            // Whenever we switch scenes, we should clear any widget selection
            this.viewModel.selection.clear();
            this._updateToCurrentScene();
        },

        _onModelChangeWidgetTabOrderMetaData() {
            this.viewModel.trigger("nestedWidgetTabOrderMetaDataChange");
        },

        _onModelChangeWidgetTabOrder() {
            this._setTabOrderData();
            this.viewModel.trigger("nestedWidgetTabOrderMetaDataChange");
        },

        _onModelChangeRemovedWidgetTabOrderMetaData() {
            this.viewModel.trigger("nestedWidgetTabOrderMetaDataChange");
        },

        _onModelChangeRemovedWidgetTabOrder() {
            this._setTabOrderData();
            this.viewModel.trigger("nestedWidgetTabOrderMetaDataChange");
        },

        /**
         * Adds an overlay (if it doesn't already exists) to the scene and sets up loading bars
         * based on the ImageModels passed in.
         *
         * @param {ImageModel[]} imageModels Array of <tt>ImageModel</tt>'s
         */
        _addUploadBars(imageModels) {
            if (!this._sceneLoadingScreen) {
                this._sceneLoadingScreen = new SceneEditorLoadingScreen();

                this._sceneLoadingScreen.once(SceneEditorLoadingScreen.UPLOADS_DONE, () => {
                    this.loadingRegion.close();
                    this._sceneLoadingScreen = null;
                });

                this.loadingRegion.show(this._sceneLoadingScreen);
            }

            this._sceneLoadingScreen.addUploads(imageModels);
        },

        _onDragOver(event) {
            event.preventDefault();
        },

        _onClickMoveSceneForwards() {
            if (this.ui.moveSceneForwards.hasClass("disabled")) {
                return;
            }

            this._moveScene("forwards", "last");
        },

        _onClickMoveSceneBackwards() {
            if (this.ui.moveSceneBackwards.hasClass("disabled")) {
                return;
            }

            this._moveScene("backwards", "first");
        },

        _updateToCurrentScaleFactor() {
            this._setBorder();
        },

        onRender() {
            this._updateToCurrentScene();
            this._setBorder();

            this.ui.widgetContainer.css({
                width: this.experienceModel.get("width"),
                height: this.experienceModel.get("height")
            });

            this.sceneWidgetCollectionView = new SceneWidgetCollectionViewsLayout({
                viewScope: this.options.viewScope,
                scene: this.model
            });

            // This is basically checking if it's a re-render (where onShow
            // won't be called). Happens when resizing the scenes.
            if (this._isShown) {
                this.sceneWidgetCollectionViewsRegion.show(this.sceneWidgetCollectionView);
                this._showBackdrop();
            } else {
                // set listeners on widgets added to the overlay and background layers
                this.listenTo(this.options.viewScope.viewModel.get("experience").get("overlayScene"), "addWidgets", this._addWidgetsHandler);
                this.listenTo(this.options.viewScope.viewModel.get("experience").get("backgroundScene"), "addWidgets", this._addWidgetsHandler);
            }

            this.sceneRulers.render();
        },

        onShow() {
            this._showHideDisableDuplicateButtons();
            this.sceneWidgetCollectionViewsRegion.show(this.sceneWidgetCollectionView);

            this._showBackdrop();

            // Listen to changes in toggling on/off
            this.listenTo(this.viewModel, "change:showMasterLayers", (model, value) => {
                this.ui.widgetsBoundary
                    .toggleClass("master-layers-on", value)
                    .toggleClass("master-layers-off", !value);
            });

            this.listenTo(this.experienceModel.get("backdrop"), "change:color change:image", this._toggleTransparentBackdropClass);
            this._toggleTransparentBackdropClass();

            this.sceneRulers.render();

            this._initializeDragSelect();
            this._initializeTabOrderListeners();
            this._setTabOrderData();
            this._toggleSceneEnabled();
            this._highlightEmailOverlappedWidgets();
        },

        _initializeTabOrderListeners() {
            this.listenTo(this.model.get("widgets"), {
                "change:action change:effects": this._removeOverlapping,
                "change:effects add": this._highlightEmailOverlappedWidgets,
                "change": this._widgetChange,
                "add": this._resetWidgetAction,
                "remove": this._deleteWidgetFromTabOrder
            });
            this.listenTo(this.experienceModel.get("backgroundScene").get("widgets"), {
                "change:action change:effects": this._removeOverlapping,
                "change:effects add": this._highlightEmailOverlappedWidgets,
                "change ": this._widgetChange,
                "add": this._resetWidgetAction,
                "remove": this._deleteWidgetFromTabOrder
            });
            this.listenTo(this.experienceModel.get("overlayScene").get("widgets"), {
                "change:action change:effects": this._removeOverlapping,
                "change:effects add": this._highlightEmailOverlappedWidgets,
                "change": this._widgetChange,
                "add": this._resetWidgetAction,
                "remove": this._deleteWidgetFromTabOrder

            });
        },

        _updateWidgetTabOrderMetaData(changedWidget, target) {
            const uuid = changedWidget.get("uuid");
            const activeLayer = this.viewModel.get("activeLayerSceneModel");
            const nextWidgetTabOrderMetaData = activeLayer.get(target)
                .map((widgetTabOrderMetaData) => {
                    if (widgetTabOrderMetaData.id === uuid) {
                        return mapWidgetMetaData({widget: changedWidget});
                    }

                    return widgetTabOrderMetaData;
                });

            activeLayer.set(target, nextWidgetTabOrderMetaData, {cause: EditorEventFilters.SUPERFICIAL});
        },

        /**
         * Updates metadata for Tab Order in response to change events on widget models.
         *
         * @param {Widget} changedWidget Widget object
         * @param {EventOptions} options Backbone event options. Set "shouldUpdateTabOrder" to false to ignore in this handler.
         */
        _widgetChange(changedWidget, options) {
            const {shouldUpdateTabOrder = true} = options;
            if (!shouldUpdateTabOrder) {
                // Updates to tabOrderMetaData are expensive, ignore high volume events by setting their "shouldUpdateTabOrder" option to false.
                // ex: text widget updates to "text" property, tabOrderMetaData will be updated when the widget selection state changes
                // so updating as a user types is unnecessary.
                return;
            }
            // UUID which is set by a computed field and isn't part of the add event.
            const isNewWidget = !!changedWidget.changed.uuid || !!changedWidget.changed.uuidSet;
            if (isNewWidget) {
                this._addWidgetToTabOrder(changedWidget);
            }
            this._updateWidgetTabOrderMetaData(changedWidget, "widgetTabOrderMetaData");
            this._updateWidgetTabOrderMetaData(changedWidget, "removedWidgetTabOrderMetaData");

            this._highlightEmailOverlappedWidgets();
        },

        _highlightEmailOverlappedWidgets() {
            const isEmailModeEnabled = this.experienceModel.get("emailModeEnabled");
            const highlight = (widgets, intersectedEmailWidgets) => {
                this.viewModel.set({hasOverlappedEmailWidgets: !!intersectedEmailWidgets.length});
                widgets.forEach((widget) => {
                    const overlapped = isEmailModeEnabled && intersectedEmailWidgets.length > 1
                        ? intersectedEmailWidgets.indexOf(widget.uuid) !== -1
                        : false;
                    widget.model.set({overlapped: overlapped});
                });
            };
            // this._resetWidgetOverlapping();
            this._getEmailActionWidgetsBoundingBoxes(highlight);
        },

        _addWidgetToTabOrder(widgetChange) {
            const activeLayer = this.viewModel.get("activeLayerSceneModel");
            const nextWidgetUUID = widgetChange.get("uuid");
            // without spreading the value changes mutate the source.
            const nextWidgetTabOrder = [...activeLayer.get("widgetTabOrder")];
            const nextWidgetTabOrderMetaData = [...activeLayer.get("widgetTabOrderMetaData")];

            if (typeof nextWidgetUUID !== "undefined" && !nextWidgetTabOrder.includes(nextWidgetUUID)) {
                nextWidgetTabOrder.push(nextWidgetUUID);
                nextWidgetTabOrderMetaData.push(mapWidgetMetaData({widget: widgetChange}));
            }

            activeLayer.set("widgetTabOrder", nextWidgetTabOrder, {cause: EditorEventFilters.SUPERFICIAL});
            activeLayer.set("widgetTabOrderMetaData", nextWidgetTabOrderMetaData, {cause: EditorEventFilters.SUPERFICIAL});
        },

        _deleteWidgetFromTargetTabOrder(uuidToDelete, target) {
            const activeLayer = this.viewModel.get("activeLayerSceneModel");
            const widgetTabOrder = target;
            const widgetTabOrderMetaData = `${target}MetaData`;
            const nextWidgetTabOrder = [...activeLayer.get(widgetTabOrder)];
            const nextWidgetTabOrderMetaData = [...activeLayer.get(widgetTabOrderMetaData)];
            const prunedWidgetTabOrder = nextWidgetTabOrder.filter((widgetUuid) => widgetUuid !== uuidToDelete);
            const prunedWidgetTabOrderMetaData = nextWidgetTabOrderMetaData.filter((widgetMetaData) =>
                widgetMetaData.id !== uuidToDelete);
            activeLayer.set(widgetTabOrder, prunedWidgetTabOrder, {cause: EditorEventFilters.SUPERFICIAL});
            activeLayer.set(widgetTabOrderMetaData, prunedWidgetTabOrderMetaData, {cause: EditorEventFilters.SUPERFICIAL});
        },

        _deleteWidgetFromTabOrder(deletedWidget) {
            const uuidToDelete = deletedWidget.get("uuid");
            this._deleteWidgetFromTargetTabOrder(uuidToDelete, "widgetTabOrder");
            this._deleteWidgetFromTargetTabOrder(uuidToDelete, "removedWidgetTabOrder");
            this._highlightEmailOverlappedWidgets();
        },

        _setTabOrderData() {
            const activeLayer = this.viewModel.get("activeLayerSceneModel");
            // Guarding against possible null widgetTabOrder data.
            const currentTabOrder = activeLayer.get("widgetTabOrder").filter((widgetTabOrder) => !!widgetTabOrder);

            const currentRemovedTabOrder = activeLayer.get("removedWidgetTabOrder");

            const defaultWidgetOrder = [...activeLayer.get("widgets").models]
                // When widgets are cloned they don't have a UUID yet.
                .filter((widget) => widget.has("uuid"));

            const widgetTabOrder = currentTabOrder.length > 0 || currentRemovedTabOrder.length > 0 ?
                currentTabOrder : defaultWidgetOrder.map((widget) => widget.get("uuid"));

            const widgetTabOrderMetaData = [];

            widgetTabOrder.forEach((widgetUUID) => {
                const targetWidget = defaultWidgetOrder.find((widget) => widget.get("uuid") === widgetUUID);
                if (targetWidget) {
                    widgetTabOrderMetaData.push(mapWidgetMetaData({widget: targetWidget}));
                }
            });

            const removedWidgetTabOrderMetaData = [];
            currentRemovedTabOrder.forEach((widgetUUID) => {
                const targetWidget = defaultWidgetOrder.find((widget) => widget.get("uuid") === widgetUUID);
                if (targetWidget) {
                    removedWidgetTabOrderMetaData.push(mapWidgetMetaData({widget: targetWidget}));
                }
            });

            activeLayer.set("widgetTabOrderMetaData", widgetTabOrderMetaData, {cause: EditorEventFilters.SUPERFICIAL});
            activeLayer.set("removedWidgetTabOrderMetaData", removedWidgetTabOrderMetaData, {cause: EditorEventFilters.SUPERFICIAL});
            if (!currentTabOrder || currentTabOrder.length === 0) {
                activeLayer.set("widgetTabOrder", widgetTabOrder, {cause: EditorEventFilters.SUPERFICIAL});
            }
        },

        _initializeDragSelect() {
            if (this.dragSelect) {
                // Stop Drag Select that are not related to current scene
                this.dragSelect.stop();
            }
            if (this.viewScope.viewModel.get("sceneNumber") === this.model.get("sceneNumber")) {
                // By default, DragSelect adds an onClick handler to each selectable element.
                // We don't want this to interfere with our own handling, so we disable it.
                // It's a bit ugly but the text widget has some fairly complex handling around
                // what happens when you click on the border vs inside the widget that it
                // seemed undesirable to import here.
                DragSelect.prototype._onClick = () => {};

                const viewportElement = this.ui.widgetsBoundary.parents(".viewport")[0];

                this.dragSelect = new DragSelect({
                    area: viewportElement,
                    selectables: this._getDragSelectSelectables(),
                    // Disable drag select when we're dragging elements to move them.
                    onDragStartBegin: (event) => {
                        if (!this.viewModel.selection.isEmpty() || event.target.getAttribute("data-dragselectable") !== "true") {
                            this.dragSelect.break();
                        }
                        this.dragSelectMouseCoords = {
                            startX: event.clientX,
                            startY: event.clientY
                        };
                    },
                    callback: (elements, event) => {
                        this.viewModel.set("isSelectingElements", true);
                        if (elements.length > 0 && event.type === "mouseup") {
                            this.dragSelectMouseCoords.endX = event.clientX;
                            this.dragSelectMouseCoords.endY = event.clientY;
                            const currentSceneNumber = this.viewModel.get("sceneNumber");
                            const selectedElementsToAppend = [];
                            // Get widgets on current layer
                            const sceneActiveLayerWidgets = this.sceneWidgetCollectionView.activeLayerWidgetCollectionView().children;
                            // Get widgets views on current layer
                            const sceneActiveLayerWidgetsViews = sceneActiveLayerWidgets._views;
                            for (let j = 0; j < elements.length; j++) {
                                const elementId = elements[j].id;
                                if (sceneActiveLayerWidgetsViews.hasOwnProperty(elementId)) {
                                    const elementCoords = elements[j].getBoundingClientRect();
                                    if (this._isElementInSelectionArea(this.dragSelectMouseCoords, elementCoords)) {
                                        const widgetSceneNumber = sceneActiveLayerWidgetsViews[elementId].model.collection.scene.get("sceneNumber");
                                        switch (widgetSceneNumber) {
                                            case currentSceneNumber:
                                            case "overlay":
                                            case "background":
                                                selectedElementsToAppend.push(sceneActiveLayerWidgetsViews[elementId]);
                                                break;
                                            default:
                                                break;
                                        }
                                    }
                                }
                            }

                            this.viewModel.selection.append(selectedElementsToAppend);
                        }
                    }
                });

                // Grouping/ungrouping changes structure of widgets so we need to
                // reload the drag select selectables list and set the selection
                // to the new composite widget.
                this.listenTo(WidgetGroup, "group", () => {
                    this._syncDragSelectFromViewModel();
                });
                this.listenTo(WidgetGroup, "ungroup", () => {
                    this._syncDragSelectFromViewModel();
                });
            }
        },

        _syncDragSelectFromViewModel() {
            this._resetDragSelect();
            this.dragSelect.clearSelection();
            this.viewModel.selection.get("widgets").forEach((widget) => {
                this.dragSelect.addSelection(widget.el);
            });
        },

        _isElementInSelectionArea(dragSelectMouseCoords, elementCoords) {
            switch (true) {
                // Selecting widgets from right to left
                case dragSelectMouseCoords.startX > this.dragSelectMouseCoords.endX:
                    switch (true) {
                        case dragSelectMouseCoords.startY > dragSelectMouseCoords.endY:
                            return this._calculateElementInRegion(
                                dragSelectMouseCoords.endX,
                                dragSelectMouseCoords.endY,
                                dragSelectMouseCoords.startX,
                                dragSelectMouseCoords.startY,
                                elementCoords
                            );
                        case dragSelectMouseCoords.startY < dragSelectMouseCoords.endY:
                            return this._calculateElementInRegion(
                                dragSelectMouseCoords.endX,
                                dragSelectMouseCoords.startY,
                                dragSelectMouseCoords.startX,
                                dragSelectMouseCoords.endY,
                                elementCoords
                            );
                        default:
                            break;
                    }
                    break;
                // Selecting widgets from left to right
                case dragSelectMouseCoords.startX < dragSelectMouseCoords.endX:
                    switch (true) {
                        // Select widgets from bottom to top
                        case dragSelectMouseCoords.startY > dragSelectMouseCoords.endY:
                            return this._calculateElementInRegion(
                                dragSelectMouseCoords.startX,
                                dragSelectMouseCoords.endY,
                                dragSelectMouseCoords.endX,
                                dragSelectMouseCoords.startY,
                                elementCoords
                            );
                        // Select widgets from top to bottom
                        case dragSelectMouseCoords.startY < dragSelectMouseCoords.endY:
                            return this._calculateElementInRegion(
                                dragSelectMouseCoords.startX,
                                dragSelectMouseCoords.startY,
                                dragSelectMouseCoords.endX,
                                dragSelectMouseCoords.endY,
                                elementCoords
                            );
                        default:
                            break;
                    }
                    break;
                default:
                    break;
            }
        },

        _calculateElementInRegion(mouseStartX, mouseStartY, mouseEndX, mouseEndY, elementCoords) {
            if (elementCoords.left > mouseStartX && elementCoords.top > mouseStartY) {
                if ((elementCoords.left + elementCoords.width) < mouseEndX && (elementCoords.top + elementCoords.height) < mouseEndY) {
                    return true;
                }
            }
        },

        _resetDragSelect() {
            const selectables = this._getDragSelectSelectables();
            if (this.dragSelect) {
                this.dragSelect.setSelectables(selectables);
            }
        },

        _showBackdrop: function() {
            this.backdropRegion.show(new BackdropViewer({
                model: this.experienceModel.get("backdrop"),
                experience: this.experienceModel
            }));
        },

        _initializeOverlayAndBackground() {
            // All other scenes than the overlay will show a backdrop
            if (!this.model.get("isOverlayScene")) {
                this.backdropRegion.show(new BackdropViewer({
                    model: this.experienceModel.get("backdrop"),
                    experience: this.experienceModel
                }));
            }
        },

        _toggleTransparentBackdropClass() {
            const backdrop = this.experienceModel.get("backdrop");
            const isTransparent = backdrop.isTransparent();
            const backgroundIsColor = !backdrop.isTypeImage();

            this.ui.widgetsBoundary.toggleClass("backdrop-is-transparent", isTransparent && backgroundIsColor);
        },

        viewportBoundingBoxProvider() {
            return this.options.viewScope.getViewportElement().get(0).getBoundingClientRect();
        },

        _setBorder() {
            const size  = this.experienceModel.getSize();
            const scale = this.viewModel.get("zoomModel").get("scale");

            this.ui.containerBorder.css({
                width: size.width * scale,
                height: size.height * scale
            });
        },

        _updateToCurrentScene() {
            const currentSceneNumber = this.viewModel.get("sceneNumber").toString();
            const sceneNumber = this.model.get("sceneNumber").toString();

            this.$el.toggleClass("current", currentSceneNumber === sceneNumber);
            this.$el.removeClass(this._sceneClassName);
            this._sceneClassName = this.model.get("id");
            this.$el.addClass(this._sceneClassName);
        },

        _isScene(position) {
            const allScenes = this.experienceModel.get("scenes");
            const sceneIndex = allScenes.indexOf(this.model);

            switch (position) {
                case "first":
                    return sceneIndex === 0;

                case "last":
                    return sceneIndex === allScenes.length - 1;

                default:
                    return false;
            }
        },

        _handleNewWidgets(widgetModels) {
            this.viewModel.set("sceneNumber", this.model.get("sceneNumber"));
            new AddWidgetsAction(this.model, widgetModels, this.viewModel).execute().done(() => {
                this._expandSidebar(widgetModels);

                widgetModels.forEach((widgetModel) => {
                    widgetModel.trigger("dropped:to:scene");

                    const eventProperties = {
                        widgetType: widgetModel.get("type"),
                        experienceId: this.experienceModel.get("id"),
                        experienceVersion: this.experienceModel.get("version"),
                        experiencePushedVersion: this.experienceModel.get("pushedVersion"),
                        sceneNumber: this.viewModel.get("activeLayerSceneModel").get("sceneNumber"),
                        triggerOrigin: "SceneEditor"
                    };

                    EditorEvents.trigger(EditorEvents.constants.ADD_WIDGET, eventProperties);
                });
            });
        },

        handleMouseDownEvent(event) {
            // We listen to the mouseDown event to ensure the dropHandler receives an event with coordinates for widget creation.
            if (this.viewScope.getCursorWidgetType()) {
                const self = this;
                event.preventDefault();

                this._dropHandler
                    .handleWidgetDropFromMouseDown(event, this.viewScope.getCursorWidgetType())
                    .done((widgetModels) => {
                        self._handleNewWidgets(widgetModels);
                    });
            }
            this.viewScope.clearCursorWidgetType();
            this.viewScope.onSceneEditorClick(this.model);
        },

        // Things dropped on scene should be always possible to convert it into widget model objects,
        // anything crazy than that should be ignored
        handleDropEvent(event) {
            const self = this;
            event.preventDefault();

            this._dropHandler
                .handle(event)
                .done((widgetModels) => {
                    self._handleNewWidgets(widgetModels);
                });
        },

        // When adding a new widget we want to (possibly) expand the right pane in the sidebar
        _expandSidebar(widgetModels) {
            if (widgetModels.length === 1) {
                const widgetType = widgetModels[0].get("type");
                const widgetDescriptor = WidgetTypes.lookup(widgetType);
                const expandProperty = widgetDescriptor.propertiesViewClass && widgetDescriptor.propertiesViewClass.EXPAND_PROPERTY;

                if (expandProperty) {
                    const propertyViewsState = this.viewModel.get("widgetPropertyViewsState");
                    propertyViewsState[expandProperty] = true;
                    this.viewModel.set("widgetPropertyViewsState", propertyViewsState);
                }
            }
        },

        /**
         * @param callback
         * @returns array
         * @private
         *
         * For Email purposes we need to separate widgets with action type "link"
         * and get bounding boxes for these widgets - so we could split experience screenshot based on these coordinates
         */
        _getEmailActionWidgetsBoundingBoxes(callback) {
            const boundingBoxes = [];
            const parseCollection = (collection, parentBoundingBox = {top: 0, left: 0}) => {
                collection.each((item) => {
                    if (item.model.get("group") && item.groupCollectionView) {
                        parseCollection(item.groupCollectionView.children, item.getBoundingBox());
                        return;
                    }
                    const action = item.model.get("action");
                    const effects = item.model.get("effects");
                    let hoverOpacity = null;
                    let hoverToggleImage = null;
                    effects.models.forEach((model) => {
                        if (model.get("triggerType") === "hover") {
                            hoverOpacity = model.get("type") === "opacity" ?  model.get("opacity") : null;
                            hoverToggleImage = model.get("type") === "imageToggle" ?  model.get("src") : null;
                        }
                    });
                    if (action && action.get("type") === "link" || hoverOpacity || hoverToggleImage) {
                        const {
                            top, left, height, width
                        } = item.getBoundingBox();
                        boundingBoxes.push({
                            uuid: item.model.get("uuid"),
                            type: item.model.get("type"),
                            actionUrl: action && action.get("url"),
                            hoverOpacity,
                            hoverToggleImage,
                            top: Math.round(top + parentBoundingBox.top),
                            left: Math.round(left + parentBoundingBox.left),
                            height: Math.round(height),
                            width: Math.round(width),
                            altText: action ? action.get("emailAltText") || action.get("url") : null,
                            label: this._getWidgetLabelForEmail(item),
                            model: item.model
                        });
                    }
                });
            };
            parseCollection(this.sceneWidgetCollectionView.sceneWidgetCollectionView.children);
            parseCollection(this.sceneWidgetCollectionView.overlayWidgetCollectionView.children);
            parseCollection(this.sceneWidgetCollectionView.backgroundWidgetCollectionView.children);

            return callback(boundingBoxes, this._getEmailWidgetsIntersections(boundingBoxes), this.model.getSceneIndex());
        },

        _getEmailWidgetsIntersections(boundingBoxes) {
            const intersectedEmailWidgets = [];
            boundingBoxes.forEach((widget)=>{
                const overlapped = boundingBoxes.find((widget2)=>{
                    return (widget2.left <= widget.left + widget.width &&
                        widget.left <= widget2.left + widget2.width &&
                        widget2.top <= widget.top + widget.height &&
                        widget.top <= widget2.top + widget2.height
                    ) && widget2.uuid !== widget.uuid;
                });
                if (overlapped) {
                    intersectedEmailWidgets.push(widget.uuid);
                }
            });
            return intersectedEmailWidgets;
        },

        _getWidgetLabelForEmail(item) {
            const type = item.model.get("type");
            const buttonType = item.model.get("buttonType");
            if (type === "button" && buttonType !== "ICON_ONLY") {
                return item.model.get("label");
            } else if (type === "text-enhanced") {
                return item.model.get("rawText");
            }
            return null;
        },


        /**
         * Handles selecting multiple widgets when they get added. Newly added widgets need to be selected manually
         * as backbone insert widgets one by one, therefore cannot be configured to select multiple
         */
        _addWidgetsHandler(widgetModels) {
            const self = this;

            _.defer(() => {
                // for a copy/paste of multiple widgets this would select the last 2+ widgets
                const lastAddedWidgets = widgetModels.map((widget) => {
                    return self.sceneWidgetCollectionView.activeLayerWidgetCollectionView().children.findByModel(widget);
                });
                this.viewScope.onWidgetsAdded(lastAddedWidgets);
                // Re-initialize drag select to pick up new widget.
                this._resetDragSelect();
            });
        },

        _selectWidgetHandler(widgetModels, showWidgetMenu) {
            if (showWidgetMenu) {
                this._expandSidebar(widgetModels);
            }
            const widgetViews = widgetModels.map((widget) => {
                return this.sceneWidgetCollectionView.activeLayerWidgetCollectionView().children.findByModel(widget);
            });
            this.viewModel.selection.select(widgetViews);
        },

        /**
         * @private
         *
         * Handles the click events triggered by the delete scene button.
         *
         * If the experience only contains a single scene, it will destroy all
         * the widgets instead of removing the scene.
         */
        _deleteSceneClickHandler() {
            if (this.ui.deleteScene.hasClass("disabled")) {
                return;
            }

            var scenes = this.experienceModel.get("scenes"),
                index = scenes.indexOf(this.model);

            log.verbose("Delete scene clicked for: " + this.model, this.model);

            // If the experience only contains a single scene...
            if (scenes.length === 1) {
                // ...remove the widgets
                log.info("Removing all widgets for scene: " + this.model);
                var widgets = _.clone(this.model.get("widgets").models); // TODO: why do we need to clone array here?
                _.invoke(widgets, "destroy");
            } else if (index !== -1) {
                log.info("Deleting scene at index " + index + ": " + this.model + " -> " + this.experienceModel, this.experienceModel);
                // Remove the scene
                this.experienceModel.removeScene(this.model); // fire!
                // Register the activity to enable undo/redo of scene disposal
                EditorActivityManager.add(new EditorActivities.SceneDeletedActivity(this.model, this.experienceModel, index));
            }
        },

        // duplicate this scene
        duplicate() {
            if (this.ui.duplicateScene.hasClass("disabled")) {
                return;
            }

            const sceneNum = this.model.getSceneIndex();
            const clone = this.model.clone({duplicate: true});
            const newSceneNum = sceneNum + 1;

            // "clone" still has old scene index at this point...
            log.info(`Duplicating scene, inserting at index ${newSceneNum}: ${clone} -> ${this.experienceModel}`, this.experienceModel);
            this.experienceModel.addScene(newSceneNum, clone);
        },

        // This does not trigger a save of variants!...
        _moveScene(direction, badPosition) {
            if (!this._isScene(badPosition)) {
                this.trigger("moveScene", direction);
            }
        },

        // Assuming the scene has the same size as the experience
        getSceneSize() {
            return this.viewScope.getExperienceSize();
        },

        onClose() {
            // close all external addons
            this._dropHandler.close();
            if (this.dragSelect) {
                this.dragSelect.stop();
            }
        },

        isCurrentScene() {
            // cast to string to handle example: `"3"` and `3`.
            // String instead of number as the background and overlay scenes have the sceneNumber `background` and `overlay` rather than a number.
            return (this.viewModel.get("sceneNumber") + "") === (this.model.get("sceneNumber") + "");
        }
    }, {
        SCENE_MARGIN
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/SceneEditor.js